import { FluidTheme, FluidThemeInterface } from '@lmig/fluid-core';
import { Component, Element, h, Host, Prop, State } from '@stencil/core';

import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import {
  FluidImageConfig,
  FluidImageTranslationOptions,
  ImageContainer,
  ImageElement,
  ImageError,
  ImageZoom,
} from './components/fluid-image.components';

/**
 * @displayName Image
 */
@Component({
  tag: 'fluid-image',
  styleUrl: 'fluid-image.scss',
  shadow: true,
})
export class FluidImage implements FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidImageTranslationOptions;
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  // ================================================================ //
  // -- Props

  /**
   * Allows all properties of an image to be configured as a single
   * object
   */
  @Prop({ mutable: true }) config?: FluidImageConfig;

  /**
   * The source of the image - corresponds to an HTML img src
   */
  @Prop({ mutable: true }) src: string;

  /**
   * The width for the image - if undefined, image will use its natural
   * width - aspect ratio will be maintained.
   */
  @Prop({ mutable: true }) width: number;

  /**
   * If true, there will be no container around the image
   */
  @Prop({ mutable: true }) noContainer = false;

  /**
   * If true, host element will be set to inline display
   */
  @Prop({ mutable: true }) inline = false;

  /**
   * Alt text for the image - if none set, the image is assumed to
   * decorative only, and the alt text will be set to empty string,
   * per https://www.w3.org/WAI/tutorials/images/decorative/
   */
  @Prop({ mutable: true }) altText = '';

  /**
   * If true, creates a hidden modal containing the image at full width, and
   * displays a click to view larger icon to toggle the modal.
   */
  @Prop({ mutable: true }) enableZoomModal: boolean;

  // ================================================================ //
  // -- State

  @State() _imageLoading = true;
  @State() _imageError = false;
  @State() _imageZoomModalOpen = false;
  @State() _imageHoveredOver = false;

  // ================================================================ //
  // -- Events

  // No Events

  // ================================================================ //
  // -- Methods

  // No Methods

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    Utils.selectiveMapConfigToLocal(this.host, this.config);
    useI18n.bind(this)();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  _elementRef;

  setImageRef(el: HTMLImageElement) {
    this._elementRef = el;
    this._elementRef.addEventListener('load', () => {
      this._imageLoading = false;
    });

    this._elementRef.addEventListener('error', () => {
      this._imageLoading = false;
      this._imageError = true;
    });
  }
  /**
   * Main Render function
   */
  render() {
    const hostStyle = {
      display: this.inline ? 'inline-block' : 'block',
    };

    return (
      <Host style={hostStyle}>
        <ImageContainer
          theme={this.theme}
          onHoverState={(hovered: boolean) =>
            (this._imageHoveredOver = hovered)
          }
          noContainer={this.noContainer}
          width={this.width}
          imageLoading={this._imageLoading}>
          <ImageElement
            imageError={this._imageError}
            fluidTranslate={this.fluidTranslate}
            i18nStore={this._i18nStore}
            altText={this.altText}
            setImageRef={(el) => this.setImageRef(el)}
            src={this.src}
          />
          <ImageError imageError={this._imageError} />
          <ImageZoom
            enabled={this.enableZoomModal && this._imageHoveredOver}
            src={this.src}
            zoomOpen={this._imageZoomModalOpen}
            toggleZoom={(zoomed: boolean) =>
              (this._imageZoomModalOpen = zoomed)
            }
          />
        </ImageContainer>
      </Host>
    );
  }
}
